/**
 * OaSIS Explorer Frontend - Language Context
 * Task 3.1: Frontend Foundation & Global Features
 * 
 * Global language state management with localStorage persistence
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { Language, LanguageContextType } from '../types';
import { languageStorage } from '../utils/localStorage';
import { createTranslationFunction } from '../utils/translations';

// Create the context
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Context provider props
interface LanguageProviderProps {
  children: ReactNode;
}

/**
 * Language Context Provider
 * Manages global language state with localStorage persistence
 */
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Initialize language from localStorage or system preference
  const [language, setLanguageState] = useState<Language>(() => {
    return languageStorage.get();
  });

  // Create translation function for current language
  const t = createTranslationFunction(language);

  /**
   * Set language with localStorage persistence
   */
  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    languageStorage.set(newLanguage);
    
    // Update document lang attribute for accessibility
    document.documentElement.lang = newLanguage;
    
    // Dispatch custom event for other components that might need to know
    window.dispatchEvent(new CustomEvent('languageChanged', { 
      detail: { language: newLanguage } 
    }));
  };

  // Set initial document language
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const contextValue: LanguageContextType = {
    language,
    setLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Hook to use language context
 * Throws error if used outside of LanguageProvider
 */
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  
  return context;
};

/**
 * Hook to get just the translation function
 * Convenience hook for components that only need translations
 */
export const useTranslation = () => {
  const { t } = useLanguage();
  return t;
};

/**
 * Higher-order component to provide language context
 */
export const withLanguage = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => (
    <LanguageProvider>
      <Component {...props} />
    </LanguageProvider>
  );
};
